import React, { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Grid,
  Link,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router";

import ProjectCard from "src/domains/project/components/ProjectCard";
import ProjectsEmptyState from "src/domains/project/components/ProjectsEmptyState";
import ProjectsToolbar, {
  type SortOption,
  type StatusFilter,
  type TeamFilter,
} from "src/domains/project/components/ProjectsToolbar";
import type { Project } from "src/domains/project/types";
import { ORGANIZATIONS, PROJECTS } from "src/platform/constants/routes";
import { useRegistryApi } from "src/platform/hooks/useRegistryApi";
import {
  getContainerStyles,
  sectionLabelSx,
} from "src/shell/styles/themeUtils";

type AugmentedProject = Project;

const ProjectsList = () => {
  const theme = useTheme();
  const { orgAlias } = useParams<{ orgAlias: string }>();

  const [projects, setProjects] = useState<AugmentedProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [teamFilter, setTeamFilter] = useState<TeamFilter>("all");
  const [sort, setSort] = useState<SortOption>("recent");
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");

  const { call: fetchProjects, endpoint } = useRegistryApi<Project[]>(
    "project",
    "listProjects",
  );

  useEffect(() => {
    if (!orgAlias || !endpoint) return;
    setLoading(true);
    setError(null);

    let cancelled = false;

    const load = async () => {
      try {
        const projectsData = await fetchProjects({
          queryParams: { organizationAlias: orgAlias },
        });

        if (cancelled) return;

        setProjects(projectsData ?? []);
      } catch {
        if (!cancelled) {
          setError("Failed to load projects. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // endpoint is a stable registry config; refetch only when orgAlias or fetchProjects changes
  }, [orgAlias, fetchProjects]);

  const teams = useMemo(() => {
    const set = new Set<string>();
    for (const p of projects) {
      const team = p.tags?.team;
      if (team) set.add(team);
    }
    return Array.from(set).sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    let list = [...projects];

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.alias.toLowerCase().includes(q) ||
          (p.description ?? "").toLowerCase().includes(q),
      );
    }

    if (statusFilter !== "all") {
      list = list.filter(
        (p) => (p.status ?? "active").toLowerCase() === statusFilter,
      );
    }

    if (teamFilter !== "all") {
      list = list.filter((p) => p.tags?.team === teamFilter);
    }

    if (sort === "name") {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      list.sort((a, b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      });
    }

    return list;
  }, [projects, search, statusFilter, teamFilter, sort]);

  const filteredCount = filteredProjects.length;
  const totalCount = projects.length;

  return (
    <Container sx={getContainerStyles(theme)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2 }}
      >
        <Link
          component={RouterLink}
          to={
            orgAlias
              ? ORGANIZATIONS.DETAILS.getPath(orgAlias)
              : ORGANIZATIONS.ROOT.path
          }
          underline="hover"
          color="inherit"
          variant="body2"
        >
          {orgAlias ?? "Organization"}
        </Link>
        <Typography variant="body2" color="text.primary">
          Projects
        </Typography>
      </Breadcrumbs>

      <Stack spacing={3}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" fontWeight={700}>
            Projects
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              aria-label="View mode"
              sx={{
                "& .MuiToggleButton-root": {
                  px: 1,
                  py: 0.5,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px !important",
                  bgcolor: (theme) => theme.palette.background.paper,
                },
              }}
            >
              <ToggleButton value="list" aria-label="List view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="Grid view">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            {orgAlias && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={RouterLink}
                to={PROJECTS.CREATE.getPath(orgAlias)}
              >
                Create project
              </Button>
            )}
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <ProjectsToolbar
          search={search}
          onSearchChange={setSearch}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          team={teamFilter}
          onTeamChange={setTeamFilter}
          sort={sort}
          onSortChange={setSort}
          teams={teams}
        />

        <Box>
          <Typography variant="subtitle2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
            {search || statusFilter !== "all" || teamFilter !== "all"
              ? `Showing ${filteredCount} of ${totalCount} projects`
              : `All projects — ${totalCount}`}
          </Typography>

          {loading ? (
            viewMode === "grid" ? (
              <Grid container spacing={2}>
                {[0, 1, 2, 3].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton
                      data-testid="skeleton"
                      variant="rounded"
                      height={180}
                      sx={{ borderRadius: "12px" }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Stack spacing={2}>
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    data-testid="skeleton"
                    variant="rounded"
                    height={76}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Stack>
            )
          ) : filteredCount === 0 && totalCount > 0 ? (
            <ProjectsEmptyState
              orgAlias={orgAlias}
              filteredMode
              onClearFilters={() => {
                setSearch("");
                setStatusFilter("all");
                setTeamFilter("all");
              }}
            />
          ) : filteredCount === 0 ? (
            <ProjectsEmptyState orgAlias={orgAlias} />
          ) : viewMode === "grid" ? (
            <Grid container spacing={2}>
              {filteredProjects.map((project) => (
                <Grid item xs={12} sm={6} md={4} key={project.alias}>
                  <ProjectCard
                    project={project}
                    orgAlias={orgAlias!}
                    variant="grid"
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Stack spacing={2}>
              {filteredProjects.map((project) => (
                <ProjectCard
                  key={project.alias}
                  project={project}
                  orgAlias={orgAlias!}
                />
              ))}
            </Stack>
          )}
        </Box>

        {!loading && filteredCount > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing 1–{filteredCount} of {totalCount} projects
          </Typography>
        )}
      </Stack>
    </Container>
  );
};

export default ProjectsList;
